/* iter-43 — Demo context. Tracks lock events + a 10-minute timer and
 * opens the DemoUpgradeModal once 3 locks or 10 minutes elapsed, whichever
 * is first. Lock counters are session-scoped (not persisted).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './auth_context';

const Ctx = createContext(null);

const LOCK_THRESHOLD = 3;
const TIMER_MS       = 10 * 60 * 1000;

export function DemoProvider({ children }) {
  const { user } = useAuth();
  const isDemo = !!user?.is_demo;
  const [lockCount, setLockCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSource, setModalSource] = useState('auto');
  const timerRef = useRef(null);

  // Start the 10-minute count when we *enter* demo mode, reset when we leave.
  useEffect(() => {
    if (!isDemo) {
      setLockCount(0);
      setModalOpen(false);
      if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null; }
      return;
    }
    if (!timerRef.current) {
      timerRef.current = window.setTimeout(() => {
        setModalSource('auto');
        setModalOpen(true);
      }, TIMER_MS);
    }
    return () => {
      if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [isDemo]);

  const recordLock = useCallback((feature = 'this feature') => {
    setLockCount((prev) => {
      const next = prev + 1;
      if (next >= LOCK_THRESHOLD) {
        setModalSource('lock');
        setModalOpen(true);
      }
      return next;
    });
    // iter-45 — best-effort telemetry POST to /api/demo/lock-event.
    try {
      const { demoApi } = require('./auth_api');
      demoApi.lockEvent?.(feature);
    } catch (err) {
      // Telemetry never blocks UX. Trace at debug level so a misbehaving
      // require()/lock-event endpoint is visible during development.
      console.debug('[demo/recordLock] telemetry POST failed:', err);
    }
    // Quiet toast so the user knows the click was registered.
    try {
      const { toast } = require('sonner');
      toast.message(`${feature} is locked in demo mode`, {
        description: 'Sign up free to unlock it — your demo notes will carry over.',
      });
    } catch (err) {
      // sonner not loaded yet (very early in app boot). Non-fatal.
      console.debug('[demo/recordLock] toast unavailable:', err);
    }
  }, []);

  const openUpgrade = useCallback((source = 'manual') => {
    setModalSource(source);
    setModalOpen(true);
  }, []);
  const closeUpgrade = useCallback(() => setModalOpen(false), []);

  const value = useMemo(() => ({
    isDemo, lockCount, modalOpen, modalSource,
    recordLock, openUpgrade, closeUpgrade,
  }), [isDemo, lockCount, modalOpen, modalSource, recordLock, openUpgrade, closeUpgrade]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDemo() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDemo must be inside DemoProvider');
  return v;
}
